import { cva, type VariantProps } from "class-variance-authority";
import * as React from "react";

import { cn } from "~/lib/utils";

const timelineVariants = cva("flex flex-col", {
  variants: {
    gap: {
      sm: "gap-3",
      md: "gap-[18px]",
      lg: "gap-6",
    },
  },
  defaultVariants: {
    gap: "md",
  },
});

const timelineIndicatorVariants = cva(
  "absolute -translate-x-1/2 rounded-full border-2",
  {
    variants: {
      size: {
        sm: "left-px top-[4px] h-[7px] w-[7px]",
        md: "left-px top-[5px] h-[9px] w-[9px]",
      },
      indicator: {
        default: "bg-muted-foreground",
        accent: "bg-primary",
        warning: "bg-yellow-500",
      },
      variant: {
        default: "border-background",
        dashed: "border-dashed border-muted-foreground bg-transparent",
      },
    },
    defaultVariants: {
      size: "md",
      indicator: "default",
      variant: "default",
    },
  }
);

/* -------------------------------- Timeline -------------------------------- */

export interface TimelineProps
  extends
    React.HTMLAttributes<HTMLDivElement>,
    VariantProps<typeof timelineVariants> {}

const Timeline = React.forwardRef<HTMLDivElement, TimelineProps>(
  ({ className, gap, ...props }, ref) => (
    <div
      ref={ref}
      className={cn(
        timelineVariants({ gap }),
        "border-l-2 border-border",
        className
      )}
      {...props}
    />
  )
);
Timeline.displayName = "Timeline";

/* ----------------------------- TimelineItem ------------------------------ */

export interface TimelineItemProps extends React.HTMLAttributes<HTMLDivElement> {}

const TimelineItem = React.forwardRef<HTMLDivElement, TimelineItemProps>(
  ({ className, ...props }, ref) => (
    <div ref={ref} className={cn("group relative", className)} {...props} />
  )
);
TimelineItem.displayName = "TimelineItem";

/* --------------------------- TimelineIndicator --------------------------- */

export interface TimelineIndicatorProps
  extends
    React.HTMLAttributes<HTMLSpanElement>,
    VariantProps<typeof timelineIndicatorVariants> {}

const TimelineIndicator = React.forwardRef<
  HTMLSpanElement,
  TimelineIndicatorProps
>(({ className, size, indicator, variant, ...props }, ref) => (
  <span
    ref={ref}
    className={cn(
      timelineIndicatorVariants({ size, indicator, variant }),
      className
    )}
    {...props}
  />
));
TimelineIndicator.displayName = "TimelineIndicator";

/* ---------------------------- TimelineContent ---------------------------- */

export interface TimelineContentProps extends React.HTMLAttributes<HTMLDivElement> {}

const TimelineContent = React.forwardRef<HTMLDivElement, TimelineContentProps>(
  ({ className, ...props }, ref) => (
    <div
      ref={ref}
      className={cn("flex min-w-0 flex-col gap-0.5 pl-5", className)}
      {...props}
    />
  )
);
TimelineContent.displayName = "TimelineContent";

export {
  Timeline,
  TimelineContent,
  TimelineIndicator,
  timelineIndicatorVariants,
  TimelineItem,
  timelineVariants,
};
